import ReactMarkdown from 'react-markdown'
import { BLOG_IMAGE_SIZE } from './blog-i18n'

/**
 * A markdown image set on its own line becomes a figure: a film plate wider
 * than the text column, captioned with its alt text as a legend. The caption
 * repeats the alt, so it is hidden from assistive tech (read once, via alt).
 */
export function PostFigure({ src, alt }) {
    return (
        <figure className="post-figure" data-reveal="rise">
            <div className="plate plate--marks">
                <img
                    src={src}
                    alt={alt}
                    width={BLOG_IMAGE_SIZE.width}
                    height={BLOG_IMAGE_SIZE.height}
                    loading="lazy"
                    decoding="async"
                />
            </div>
            {alt ? (
                <figcaption className="legend" aria-hidden="true">
                    {alt}
                </figcaption>
            ) : null}
        </figure>
    )
}

/** @param {{ type: string, value?: string }} child */
function isBlankText(child) {
    return child.type === 'text' && !child.value.trim()
}

/** A paragraph holding only an image renders as a figure (a figure may not sit inside <p>). */
function Paragraph({ node, children }) {
    const content = (node?.children ?? []).filter((child) => !isBlankText(child))
    const only = content.length === 1 ? content[0] : null
    if (only?.type === 'element' && only.tagName === 'img') {
        const { src = '', alt = '' } = only.properties ?? {}
        return <PostFigure src={String(src)} alt={String(alt)} />
    }
    return <p>{children}</p>
}

const MARKDOWN_COMPONENTS = {
    // The page title is the only h1; the article's own top heading becomes its lede.
    h1: ({ children }) => <h2 className="post-lede">{children}</h2>,
    p: Paragraph,
    img: ({ src, alt }) => <img src={src} alt={alt ?? ''} loading="lazy" decoding="async" />,
    table: ({ children }) => (
        <div className="post-table">
            <table>{children}</table>
        </div>
    ),
}

/**
 * The long-form column. Output elements are direct children of `.post-body`
 * (a subgrid), so text sits on the 66ch measure and figures break out wider.
 *
 * @param {{ markdown: string }} props
 */
export default function PostBody({ markdown }) {
    return (
        <div className="post-body">
            <ReactMarkdown components={MARKDOWN_COMPONENTS}>{markdown}</ReactMarkdown>
        </div>
    )
}
